const buildNavigationListItem = (
  item,
  { payloadKey = 'item_id', includeClickAction = false } = {}
) => {
  const mainContent = {
    title: item.title || item.label || 'Item',
    ...(item.description ? { description: item.description } : {}),
  };

  const id = item.id || item.value || 'item_1';
  const node = {
    id,
    'main-content': mainContent,
  };

  // Meta NavigationList images belong in `start`, as base64 (not https URLs).
  if (item.image_src && !/^https?:\/\//i.test(item.image_src)) {
    node.start = {
      image: item.image_src,
      'alt-text': item.image_alt || item.title || 'Image',
    };
  }

  // Dynamic lists: per-item action with concrete id (Meta does not always interpolate ${id}).
  if (includeClickAction) {
    const key = sanitizeDataKey(payloadKey, 'item_id');
    node['on-click-action'] = {
      name: 'data_exchange',
      payload: { [key]: String(id) },
    };
  }

  return node;
};

/** Meta data keys must be identifiers: ${data.your_value} — no spaces. */
export const sanitizeDataKey = (key, fallback = 'items') => {
  const cleaned = String(key || '')
    .trim()
    .replace(/[^a-zA-Z0-9_]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');
  return cleaned || fallback;
};

const buildSelectionDataSource = (component, isDynamic = false) => {
  if (isDynamic && component.data_mode === 'dynamic') {
    const key = sanitizeDataKey(component.data_source_key, 'options');
    return `\${data.${key}}`;
  }

  return (component.options || []).map((opt) => ({
    id: opt.value || opt.id,
    title: opt.label || opt.value,
  }));
};

const buildSelectionOptionsSchema = (component) => {
  const key = sanitizeDataKey(component.data_source_key, 'options');
  const examples =
    component.example_options ||
    (component.options || []).map((opt) => ({
      id: opt.value,
      title: opt.label,
    })) || [
      { id: 'option_1', title: 'Option 1' },
      { id: 'option_2', title: 'Option 2' },
    ];

  return {
    [key]: {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          id: { type: 'string' },
          title: { type: 'string' },
        },
      },
      __example__: examples.map((opt) => ({
        id: opt.id || opt.value,
        title: opt.title || opt.label,
      })),
    },
  };
};

const parseSelectionMetaComponent = (node, type) => {
  const dataSource = node['data-source'];
  const isDynamicSource = typeof dataSource === 'string' && dataSource.startsWith('${data.');
  const keyMatch = isDynamicSource ? dataSource.match(/\$\{data\.([^}]+)\}/) : null;

  return {
    id: node.name,
    type,
    name: node.name,
    label: node.label,
    required: node.required !== false,
    data_mode: isDynamicSource ? 'dynamic' : 'static',
    data_source_key: keyMatch?.[1] || 'options',
    options: isDynamicSource
      ? []
      : (dataSource || []).map((opt) => ({
          value: opt.id,
          label: opt.title,
        })),
    example_options: isDynamicSource
      ? (Array.isArray(dataSource) ? dataSource : []).map((opt) => ({
          id: opt.id,
          title: opt.title,
        }))
      : undefined,
  };
};

const FORM_FIELD_TYPES = [
  'radio',
  'checkbox',
  'dropdown',
  'text_input',
  'text_area',
  'text_email',
  'text_phone',
  'date_picker',
  'opt_in',
];

const getFormFieldsFromComponents = (components = []) =>
  components.filter((component) => FORM_FIELD_TYPES.includes(component.type) && component.name);

/**
 * NavigationList tap keys (e.g. event_id) must be carried into later screens'
 * data model + complete payload as ${data.event_id}. The tenant API must echo
 * the same key in the next screen's data response.
 */
const getNavigationPayloadFieldsFromComponents = (components = []) => {
  const fields = [];
  const seen = new Set();

  (components || []).forEach((component) => {
    if (component?.type !== 'navigation_list') {
      return;
    }
    const name = sanitizeDataKey(component.payload_key, 'item_id');
    if (seen.has(name)) {
      return;
    }
    seen.add(name);
    // Shape matches form fields so buildCarriedFieldSchema treats it as a string.
    fields.push({ name, type: 'text_input' });
  });

  return fields;
};

const buildFormPayloadFromComponents = (components = []) => {
  const payload = {};
  getFormFieldsFromComponents(components).forEach((component) => {
    payload[component.name] = `\${form.${component.name}}`;
  });
  return payload;
};

/** Fields passed via navigate must exist on the next screen's data model. */
const buildCarriedFieldSchema = (component) => {
  if (component.type === 'checkbox') {
    return {
      type: 'array',
      items: { type: 'string' },
      __example__: ['option_1'],
    };
  }
  if (component.type === 'opt_in') {
    return {
      type: 'boolean',
      __example__: true,
    };
  }
  return {
    type: 'string',
    __example__: 'example',
  };
};

const buildCarriedDataSchema = (carriedFields = []) => {
  const data = {};
  carriedFields.forEach((field) => {
    if (field?.name) {
      data[field.name] = buildCarriedFieldSchema(field);
    }
  });
  return data;
};

const buildActionPayload = (screenComponents = [], carriedFields = [], payloadKeys = []) => {
  const payload = {};
  const formFieldNames = new Set(
    getFormFieldsFromComponents(screenComponents).map((component) => component.name)
  );

  // Values from previous screens (received via navigate payload).
  carriedFields.forEach((field) => {
    if (field?.name) {
      payload[field.name] = `\${data.${field.name}}`;
    }
  });

  // Values entered on the current screen.
  Object.assign(payload, buildFormPayloadFromComponents(screenComponents));

  // Optional custom form field → payload key mappings (skip stale/unknown fields).
  (payloadKeys || []).forEach((key) => {
    if (!key?.field || !key?.payload_key) {
      return;
    }
    if (!formFieldNames.has(key.field)) {
      return;
    }
    if (payload[key.payload_key] !== undefined) {
      return;
    }
    if (key.payload_key === key.field) {
      return;
    }
    payload[key.payload_key] = `\${form.${key.field}}`;
  });

  return payload;
};

const buildFooterAction = (
  component,
  {
    isDynamic = false,
    isTerminal = false,
    screenComponents = [],
    carriedFields = [],
    nextScreenId = null,
  } = {}
) => {
  const payload = buildActionPayload(
    screenComponents,
    carriedFields,
    component.payload_keys
  );

  // Meta requires terminal screens to end with `complete` (not data_exchange/navigate).
  if (isTerminal) {
    return { name: 'complete', payload };
  }

  const action = component.action || (isDynamic ? 'data_exchange' : 'navigate');

  if (action === 'data_exchange') {
    return { name: 'data_exchange', payload };
  }

  const navigateTo = nextScreenId || component.navigate_screen;
  if ((action === 'navigate' || !isDynamic) && navigateTo) {
    return {
      name: 'navigate',
      next: { type: 'screen', name: String(navigateTo).toUpperCase() },
      payload,
    };
  }

  if (isDynamic) {
    return { name: 'data_exchange', payload };
  }

  return { name: 'complete', payload };
};

const buildDynamicDataSchemaForComponent = (component) => {
  if (component.type === 'navigation_list') {
    const key = sanitizeDataKey(component.data_source_key, 'items');
    const payloadKey = sanitizeDataKey(component.payload_key, 'item_id');
    const examples = (component.data_mode === 'dynamic'
      ? component.example_items
      : component.items) || [];
    const isDynamicList = component.data_mode === 'dynamic';

    return {
      [key]: {
        type: 'array',
        items: {
          type: 'object',
          properties: {
            id: { type: 'string' },
            'main-content': {
              type: 'object',
              properties: {
                title: { type: 'string' },
                description: { type: 'string' },
              },
            },
            start: {
              type: 'object',
              properties: {
                image: { type: 'string' },
                'alt-text': { type: 'string' },
              },
            },
            ...(isDynamicList
              ? {
                  'on-click-action': {
                    type: 'object',
                    properties: {
                      name: { type: 'string' },
                      payload: { type: 'object' },
                    },
                  },
                }
              : {}),
          },
        },
        __example__: examples.map((item) =>
          buildNavigationListItem(item, {
            payloadKey,
            includeClickAction: isDynamicList,
          })
        ),
      },
    };
  }

  if (
    (component.type === 'radio' ||
      component.type === 'checkbox' ||
      component.type === 'dropdown') &&
    component.data_mode === 'dynamic'
  ) {
    return buildSelectionOptionsSchema(component);
  }

  return {};
};

const convertComponentToFlowFormat = (
  component,
  {
    isDynamic = false,
    isTerminal = false,
    screenComponents = [],
    carriedFields = [],
    nextScreenId = null,
  } = {}
) => {
  switch (component.type) {
    case 'text_large_heading':
      return {
        type: 'TextHeading',
        text: component.text || 'Heading',
      };
    case 'text_small_heading':
      return {
        type: 'TextSubheading',
        text: component.text || 'Subheading',
      };
    case 'text_body':
      return {
        type: 'TextBody',
        text: component.text || '',
      };
    case 'text_caption':
      return {
        type: 'TextCaption',
        text: component.text || '',
      };
    case 'text_input':
      return {
        type: 'TextInput',
        name: component.name,
        label: component.placeholder || 'Enter text',
        'input-type': 'text',
        required: component.required !== false,
        ...(component.max_length && {
          'max-length': parseInt(component.max_length, 10),
        }),
      };
    case 'text_area':
      return {
        type: 'TextArea',
        name: component.name,
        label: component.label || 'Enter text',
        required: component.required !== false,
        ...(component.max_length && {
          'max-length': parseInt(component.max_length, 10),
        }),
      };
    case 'email_input':
    case 'text_email':
      return {
        type: 'TextInput',
        name: component.name,
        label: component.placeholder || 'Enter email',
        'input-type': 'email',
        required: component.required !== false,
      };
    case 'phone_input':
    case 'text_phone':
      return {
        type: 'TextInput',
        name: component.name,
        label: component.placeholder || 'Enter phone',
        'input-type': 'phone',
        required: component.required !== false,
      };
    case 'radio':
      return {
        type: 'RadioButtonsGroup',
        name: component.name,
        label: component.label || 'Select option',
        required: component.required !== false,
        'data-source': buildSelectionDataSource(component, isDynamic),
      };
    case 'checkbox':
      return {
        type: 'CheckboxGroup',
        name: component.name,
        label: component.label || 'Select options',
        required: component.required !== false,
        'data-source': buildSelectionDataSource(component, isDynamic),
      };
    case 'dropdown':
      return {
        type: 'Dropdown',
        name: component.name,
        label: component.label || 'Select option',
        required: component.required !== false,
        'data-source': buildSelectionDataSource(component, isDynamic),
      };
    case 'navigation_list': {
      const listKey = sanitizeDataKey(component.data_source_key, 'items');
      const payloadKey = sanitizeDataKey(component.payload_key, 'item_id');
      const isDynamicList = isDynamic && component.data_mode === 'dynamic';
      const listItems = isDynamicList
        ? `\${data.${listKey}}`
        : (component.items || component.example_items || []).map((item) =>
            buildNavigationListItem(item, { payloadKey, includeClickAction: false })
          );

      const node = {
        type: 'NavigationList',
        name: component.name,
        label: component.label || 'Select an option',
        'list-items': listItems,
      };

      // Dynamic lists: per-item on-click-action is injected at runtime with the real id
      // (Meta often sends literal "${id}" for component-level payload).
      // Static lists: component-level action is fine.
      if (!isDynamicList) {
        const payload = { [payloadKey]: `\${id}` };
        if (isTerminal) {
          node['on-click-action'] = { name: 'complete', payload };
        } else {
          const clickAction = component.click_action || (isDynamic ? 'data_exchange' : 'navigate');
          if (clickAction === 'navigate' && nextScreenId) {
            node['on-click-action'] = {
              name: 'navigate',
              next: { type: 'screen', name: String(nextScreenId).toUpperCase() },
              payload,
            };
          } else {
            node['on-click-action'] = { name: 'data_exchange', payload };
          }
        }
      }

      return node;
    }
    case 'date_picker':
      return {
        type: 'DatePicker',
        name: component.name,
        label: component.label || 'Select date',
        required: component.required !== false,
      };
    case 'opt_in':
      return {
        type: 'OptIn',
        name: component.name,
        label: component.label || 'I agree',
        required: component.required !== false,
      };
    case 'image':
      return {
        type: 'Image',
        src: component.src || '',
        ...(component.alt && { 'alt-text': component.alt }),
      };
    case 'button':
      return {
        type: 'Footer',
        label: component.label || (isTerminal ? 'Submit' : 'Continue'),
        'on-click-action': buildFooterAction(component, {
          isDynamic,
          isTerminal,
          screenComponents,
          carriedFields,
          nextScreenId,
        }),
      };
    default:
      return null;
  }
};

/**
 * Meta only allows forward routes. Entry screen = first screen (no inbound edges).
 * Always derive routing from current screen order — ignore stale next_step_id.
 */
const buildRoutingModel = (screens) => {
  const routing = {};
  screens.forEach((screen, index) => {
    const screenId = screen.id.toUpperCase();
    if (index < screens.length - 1) {
      routing[screenId] = [screens[index + 1].id.toUpperCase()];
    } else {
      routing[screenId] = [];
    }
  });
  return routing;
};

/** Prefer explicit navigate target only if it is a later screen (forward). */
const resolveForwardScreenId = (screens, currentIndex, preferredId) => {
  const nextInOrder = screens[currentIndex + 1]?.id || null;
  if (!preferredId) return nextInOrder;

  const preferredIndex = screens.findIndex(
    (screen) => String(screen.id).toLowerCase() === String(preferredId).toLowerCase()
  );
  if (preferredIndex > currentIndex) {
    return screens[preferredIndex].id;
  }

  return nextInOrder;
};

export const META_FLOW_JSON_VERSION = '7.3';

/**
 * Meta: a screen with NavigationList may only contain that list (max 1).
 * Footer and other components are not allowed on the same screen.
 */
const resolveExportComponents = (components = []) => {
  const navigationLists = components.filter((c) => c.type === 'navigation_list').slice(0, 1);
  if (navigationLists.length > 0) {
    return navigationLists;
  }
  return components;
};

export const buildFlowJson = (screens, { isDynamic = false } = {}) => {
  const resolvedScreens = screens.map((screen) => ({
    ...screen,
    components: resolveExportComponents(screen.components || []),
  }));

  // Form fields + NavigationList tap keys (used for next-screen data models + complete payload).
  const fieldsByScreen = resolvedScreens.map((screen) =>
    getFormFieldsFromComponents(screen.components)
  );
  const navigationPayloadFieldsByScreen = resolvedScreens.map((screen) =>
    getNavigationPayloadFieldsFromComponents(screen.components)
  );

  // Values from earlier screens must be declared on later screens' `data` and
  // referenced in complete as ${data.<name>} (tenant API must echo them).
  const carriedFieldsByScreen = resolvedScreens.map((_, index) => {
    const carried = [];
    const seen = new Set();
    for (let i = 0; i < index; i += 1) {
      [...fieldsByScreen[i], ...navigationPayloadFieldsByScreen[i]].forEach((field) => {
        if (!field?.name || seen.has(field.name)) {
          return;
        }
        seen.add(field.name);
        carried.push(field);
      });
    }
    return carried;
  });

  const flowJson = {
    version: META_FLOW_JSON_VERSION,
    routing_model: buildRoutingModel(screens),
    screens: resolvedScreens.map((screen, index) => {
      const isLastScreen = index === screens.length - 1;
      const components = screen.components || [];
      const carriedFields = carriedFieldsByScreen[index];

      const screenData = {
        ...buildCarriedDataSchema(carriedFields),
      };

      if (isDynamic) {
        components.forEach((component) => {
          Object.assign(screenData, buildDynamicDataSchemaForComponent(component));
        });
      }

      const nextScreenId = resolveForwardScreenId(screens, index, null);

      const screenNode = {
        id: screen.id.toUpperCase(),
        title: screen.title,
        terminal: isLastScreen,
        data: screenData,
        layout: {
          type: 'SingleColumnLayout',
          children: components
            .map((component) =>
              convertComponentToFlowFormat(component, {
                isDynamic,
                isTerminal: isLastScreen,
                screenComponents: components,
                carriedFields,
                nextScreenId: resolveForwardScreenId(
                  screens,
                  index,
                  component.navigate_screen
                ),
              })
            )
            .filter(Boolean),
        },
      };

      // Meta only allows `success` on terminal screens.
      if (isLastScreen) {
        screenNode.success = true;
      }

      return screenNode;
    }),
  };

  if (isDynamic) {
    flowJson.data_api_version = '3.0';
  }

  return flowJson;
};

const parseMetaComponent = (node) => {
  if (!node || !node.type) return null;

  if (node.type === 'TextHeading') {
    return {
      id: node.name || `heading_${Date.now()}`,
      type: 'text_large_heading',
      text: node.text || '',
      max_length: 80,
    };
  }

  if (node.type === 'TextSubheading') {
    return {
      id: node.name || `subheading_${Date.now()}`,
      type: 'text_small_heading',
      text: node.text || '',
      max_length: 80,
    };
  }

  if (node.type === 'TextBody') {
    return {
      id: node.name || `body_${Date.now()}`,
      type: 'text_body',
      text: node.text || '',
      max_length: 4096,
    };
  }

  if (node.type === 'TextCaption') {
    return {
      id: node.name || `caption_${Date.now()}`,
      type: 'text_caption',
      text: node.text || '',
      max_length: 4096,
    };
  }

  if (node.type === 'TextArea') {
    return {
      id: node.name,
      type: 'text_area',
      name: node.name,
      label: node.label,
      max_length: node['max-length'],
      required: node.required !== false,
    };
  }

  if (node.type === 'TextInput') {
    const inputType = node['input-type'] || 'text';
    if (inputType === 'email') {
      return {
        id: node.name,
        type: 'text_email',
        name: node.name,
        placeholder: node.label,
        required: node.required !== false,
      };
    }
    if (inputType === 'phone') {
      return {
        id: node.name,
        type: 'text_phone',
        name: node.name,
        placeholder: node.label,
        required: node.required !== false,
      };
    }
    return {
      id: node.name,
      type: 'text_input',
      name: node.name,
      placeholder: node.label,
      max_length: node['max-length'],
      required: node.required !== false,
    };
  }

  if (node.type === 'RadioButtonsGroup') {
    return parseSelectionMetaComponent(node, 'radio');
  }

  if (node.type === 'NavigationList') {
    const listItems = node['list-items'];
    const isDynamicList = typeof listItems === 'string' && listItems.startsWith('${data.');
    const keyMatch = isDynamicList ? listItems.match(/\$\{data\.([^}]+)\}/) : null;
    const clickAction = node['on-click-action'];
    const payloadKey = clickAction?.payload
      ? Object.keys(clickAction.payload)[0]
      : 'selected_id';

    const mapItem = (item) => ({
      id: item.id,
      title: item['main-content']?.title || '',
      description: item['main-content']?.description || '',
      image_src: item.start?.image || item['main-content']?.image?.src || '',
      image_alt:
        item.start?.['alt-text'] ||
        item['main-content']?.image?.['alt-text'] ||
        '',
    });

    return {
      id: node.name,
      type: 'navigation_list',
      name: node.name,
      label: node.label,
      data_mode: isDynamicList ? 'dynamic' : 'static',
      data_source_key: keyMatch?.[1] || 'items',
      payload_key: payloadKey,
      click_action: clickAction?.name || 'data_exchange',
      navigate_screen: clickAction?.next?.name?.toLowerCase(),
      example_items: isDynamicList ? [] : (listItems || []).map(mapItem),
      items: isDynamicList ? [] : (listItems || []).map(mapItem),
    };
  }

  if (node.type === 'CheckboxGroup') {
    return parseSelectionMetaComponent(node, 'checkbox');
  }

  if (node.type === 'Dropdown') {
    return parseSelectionMetaComponent(node, 'dropdown');
  }

  if (node.type === 'DatePicker') {
    return {
      id: node.name,
      type: 'date_picker',
      name: node.name,
      label: node.label,
      required: node.required !== false,
    };
  }

  if (node.type === 'OptIn') {
    return {
      id: node.name,
      type: 'opt_in',
      name: node.name,
      label: node.label,
      required: node.required !== false,
    };
  }

  if (node.type === 'Image') {
    return {
      id: node.name || `image_${Date.now()}`,
      type: 'image',
      src: node.src || '',
      alt: node['alt-text'] || node.alt || 'Image',
    };
  }

  if (node.type === 'Footer') {
    const clickAction = node['on-click-action'];
    const actionName = clickAction?.name || 'complete';

    return {
      id: node.name || 'footer_action',
      type: 'button',
      name: node.name || 'footer_action',
      label: node.label,
      action: actionName,
      navigate_screen: clickAction?.next?.name?.toLowerCase(),
      payload_keys: clickAction?.payload
        ? Object.entries(clickAction.payload)
            .map(([payloadKey, value]) => {
              const fieldMatch =
                typeof value === 'string' ? value.match(/\$\{form\.([^}]+)\}/) : null;
              if (!fieldMatch?.[1]) {
                return null;
              }
              return {
                payload_key: payloadKey,
                field: fieldMatch[1],
              };
            })
            .filter(Boolean)
        : [],
    };
  }

  return null;
};

const enrichComponentFromScreenData = (component, screenData) => {
  if (!component || !screenData) return component;

  if (component.type === 'navigation_list' && component.data_mode === 'dynamic') {
    const schema = screenData[component.data_source_key];
    const examples = schema?.__example__ || [];
    if (examples.length) {
      return {
        ...component,
        example_items: examples.map((item) => ({
          id: item.id,
          title: item['main-content']?.title || '',
          description: item['main-content']?.description || '',
          image_src: item.start?.image || item['main-content']?.image?.src || '',
          image_alt:
            item.start?.['alt-text'] ||
            item['main-content']?.image?.['alt-text'] ||
            '',
        })),
      };
    }
  }

  if (
    (component.type === 'radio' ||
      component.type === 'checkbox' ||
      component.type === 'dropdown') &&
    component.data_mode === 'dynamic'
  ) {
    const schema = screenData[component.data_source_key];
    const examples = schema?.__example__ || [];
    if (examples.length) {
      return {
        ...component,
        example_options: examples.map((opt) => ({
          id: opt.id,
          title: opt.title,
        })),
      };
    }
  }

  return component;
};

export const parseFlowJson = (raw) => {
  if (!raw) return [];

  const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
  if (!parsed?.screens || !Array.isArray(parsed.screens)) return [];

  const routing = parsed.routing_model || {};

  return parsed.screens.map((screen) => {
    const children = screen.layout?.children || [];
    const components = children
      .map(parseMetaComponent)
      .filter(Boolean)
      .map((component) => enrichComponentFromScreenData(component, screen.data));

    const screenId = screen.id?.toLowerCase() || screen.id;
    const nextRoutes = routing[screen.id] || routing[screen.id?.toUpperCase()] || [];

    return {
      id: screenId,
      title: screen.title || screenId,
      next_step_id: nextRoutes[0]?.toLowerCase() || nextRoutes[0],
      components,
    };
  });
};

export const extractMetaFlowErrors = (data) => {
  if (!data?.validation_errors?.length) return [];
  return data.validation_errors.map(
    (err) => `${err.error_type || 'error'}: ${err.message || 'Validation failed'}`
  );
};

export const DEFAULT_META_FLOW_SCREEN = {
  id: 'welcome',
  title: 'Welcome',
  components: [
    {
      id: 'welcome_heading',
      type: 'text_large_heading',
      text: 'Welcome',
      max_length: 80,
    },
    {
      id: 'continue_btn',
      type: 'button',
      name: 'continue_btn',
      label: 'Complete',
      action: 'complete',
    },
  ],
};

export const buildDefaultFlowJson = ({ isDynamic = false } = {}) =>
  buildFlowJson([DEFAULT_META_FLOW_SCREEN], { isDynamic });
